import { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { logger } from '@pkg/logger';
import type { SurpriseBag } from '@pkg/types/surpriseBag';
import { BAG_AVAILABILITY, SURPRISE_BAG_NEARBY_BAGS_LIMIT } from '@pkg/types/surpriseBag';
import { getBagAvailability } from '@pkg/utilities/surprisebag';

/**
 * Picks the bags to show in the "nearby bags" section of a bag detail page.
 * Bags that can be reserved right now come first; when there are fewer than `limit`,
 * the nearest unavailable bags fill the remaining slots. Never includes the bag being
 * viewed, deduplicates by item id and keeps the API's distance order within each group.
 */
export function selectNearbyBags(
  bags: SurpriseBag[] | null | undefined,
  currentItemId: string | null | undefined,
  limit: number = SURPRISE_BAG_NEARBY_BAGS_LIMIT,
): SurpriseBag[] {
  if (!Array.isArray(bags) || limit <= 0) { return []; }

  const seen = new Set<string>();
  const available: SurpriseBag[] = [];
  const unavailable: SurpriseBag[] = [];

  for (const bag of bags) {
    const itemId = bag?.item?.itemId;
    if (!itemId || itemId === currentItemId || seen.has(itemId)) { continue; }
    seen.add(itemId);

    if (getBagAvailability(bag) === BAG_AVAILABILITY.AVAILABLE) {
      available.push(bag);
    } else {
      unavailable.push(bag);
    }
  }

  return [...available, ...unavailable].slice(0, limit);
}

export function toKebab(str: string, localeCode?: string): string {
  const safeLocale = (localeCode ?? '').toLowerCase();
  const isJapanese = safeLocale === LOCALE_CODE.JA;
  const base = str.toLowerCase().trim();
  const hasNonAscii = /[\u0080-\u{10FFFF}]/u.test(base);
  const isEnglishLocale = safeLocale.startsWith('en');

  if (isJapanese) {
    return base
      // keep unicode letters/numbers for Japanese locale
      .replace(/[^\p{L}\p{N}\s-]/gu, '')
      .replace(/\s+/g, '-') // replace spaces with hyphens
      .replace(/-+/g, '-') // collapse multiple hyphens
      .replace(/^-+|-+$/g, ''); // remove leading or trailing hyphens
  }

  return base
    .normalize('NFD') // separate accents (e.g. é → e + ́)
    .replace(/[\u0300-\u036F]/g, '') // remove accent marks
    .replace(/[^a-z0-9\s-]/g, '') // remove special characters except letters, numbers, spaces, and hyphens
    .replace(/\s+/g, hasNonAscii || !isEnglishLocale ? '' : '-') // replace spaces
    .replace(/-+/g, '-') // collapse multiple hyphens
    .replace(/^-+|-+$/g, '');
}

export function surpriseBagUrlHelper(
  itemId: string,
  localeCode: string,
  city?: string,
  displayName?: string,
  category?: string,
  itemName?: string,
  leadingSlash?: boolean,
): string {
  const safeLocale = localeCode ? `${localeCode.toLowerCase()}` : '';
  const safeCity = toKebab(city || 'unknown', localeCode);
  const safeDisplay = toKebab(displayName || 'unknown', localeCode);
  const safeCategory = toKebab(category || 'unknown', localeCode);
  const safeItemName = toKebab(itemName || 'item', localeCode);
  const leadingSlashPrefix = leadingSlash ? '/' : '';

  const item = itemName ? `${safeItemName}-${itemId}` : itemId;

  return `${leadingSlashPrefix}${safeLocale}/find/${safeCity}/${safeDisplay}/${safeCategory}/${item}`;
}

/**
 * Maps localization codes to their corresponding tech codes.
 * @returns A map of localization codes to their corresponding tech codes.
 */
export function getLocalizationMapFromTech() {
  return {
    'de-AT': 'de-at',
    'nl-BE': 'nl-be',
    'fr-BE': 'fr-be',
    'da-DK': 'da',
    'fr-FR': 'fr',
    'de-DE': 'de',
    'it-IT': 'it',
    'nb-NO': 'no',
    'pl-PL': 'pl',
    'pt-PT': 'pt',
    'es-ES': 'es',
    'sv-SE': 'sv',
    'de-CH': 'de-ch',
    'fr-CH': 'fr-ch',
    'it-CH': 'it-ch',
    'nl-NL': 'nl',
    'en-GB': 'en-gb',
    'en-US': 'en-us',
    'en-CA': 'en-ca',
    'fr-CA': 'fr-ca',
    'en-IE': 'en-ie',
    'en-LU': 'en-lu',
    'fr-LU': 'fr-lu',
    'en-AU': 'en-au',
    'en-NZ': 'en-nz',
    'ja-JP': 'ja',
  };
}

/**
 * Returns the site locale a bag "belongs to" (the same locale the surprise bag sitemap
 * lists it under), derived from the bag's `localizationCode` (e.g. `nb-NO` -> `no`).
 * Returns null if the code is missing or not a supported site locale.
 */
export function getSurpriseBagHomeLocale(localizationCode?: string | null): string | null {
  if (!localizationCode) { return null; }

  const wanted = localizationCode.trim().toLowerCase();
  const match = Object.entries(getLocalizationMapFromTech())
    .find(([techCode]) => techCode.toLowerCase() === wanted);

  return match ? match[1] : null;
}

/** The sitemap cron strips every whitespace character from each CSV column before slugging. */
const stripWhitespace = (value?: string | null): string => (value ?? '').replace(/\s+/g, '');

/**
 * The one URL a bag should be indexed under: its home-locale URL, built from the bag's own data
 * with `surpriseBagUrlHelper`, exactly as the surprise bag sitemap cron builds it.
 *
 * Bags resolve by the trailing item id, so any URL (a different locale prefix, or made-up slug
 * segments like `/es/foo/bar/123`) renders the same bag. Deriving the canonical from the bag data
 * rather than from the requested URL collapses all of those onto this single path.
 *
 * Returns null when the home locale or item id can't be worked out (caller keeps its default).
 */
export function getSurpriseBagCanonicalPath(bag?: SurpriseBag | null): string | null {
  const itemId = bag?.item?.itemId;
  const homeLocale = getSurpriseBagHomeLocale(bag?.localizationCode);
  if (!bag || !itemId || !homeLocale) { return null; }

  // Same inputs and same whitespace handling as the sitemap generator, so the URLs match.
  return surpriseBagUrlHelper(
    stripWhitespace(itemId),
    homeLocale,
    stripWhitespace(bag.store?.storeLocation?.address?.city),
    stripWhitespace(bag.displayName || bag.store?.storeName),
    stripWhitespace(bag.item?.itemCategory),
    stripWhitespace(bag.item?.name),
    true,
  );
}

/* DEEPLINK HELPER */

const oneLinkURL = 'https://tgtg.onelink.me/OGjG';

type ParamRule = {
  keys?: string[];
  paramKey?: string;
  defaultValue?: string;
};

const paramRules: ParamRule[] = [
  { keys: ['af_source'], paramKey: 'af_source', defaultValue: 'website_surprise_bag' },
  { keys: ['pid'], paramKey: 'pid', defaultValue: 'web-indexed' },
  { keys: ['itemId'], paramKey: 'af_sub1' },
  { keys: ['itemName'], paramKey: 'af_sub2' },
  { keys: ['city'], paramKey: 'af_sub3' },
  { keys: ['displayName'], paramKey: 'af_sub4' },
  { keys: ['category'], paramKey: 'af_sub5' },
  { keys: ['qrCode'], paramKey: 'af_channel' },
  { keys: ['deep_link_value'], paramKey: 'deep_link_value' },
  { paramKey: 'af_ss_ui', defaultValue: 'true' },
  { paramKey: 'af_ss_gtm_ui', defaultValue: 'true' },
];

// Utility to build the deep link
export function buildDeepLink(params: Record<string, string | undefined>): string {
  try {
    const queryParams = paramRules.map((rule) => {
      const queryKey = rule.paramKey ?? rule.keys?.[0];
      if (!queryKey) { return null; }

      let value: string | undefined;

      // Special logic: deep_link_value comes from item_id
      if (queryKey === 'deep_link_value') {
        value = `http://share.toogoodtogo.com/stores/details/${params.itemId}` || rule.defaultValue;
      } else if (queryKey === 'af_channel') {
        const rawValue = rule.keys?.find(k => params[k] !== undefined)
          ? params[rule.keys!.find(k => params[k] !== undefined)!]
          : rule.defaultValue;
        value = rawValue === 'true' ? 'qr_desktop' : rawValue === 'false' ? 'cta_mobile' : rawValue;
      } else {
        value =
          rule.keys?.find(k => params[k] !== undefined)
            ? params[rule.keys!.find(k => params[k] !== undefined)!]
            : rule.defaultValue;
      }

      return value ? `${encodeURIComponent(queryKey)}=${encodeURIComponent(value)}` : null;
    }).filter(Boolean);

    return `${oneLinkURL}?${queryParams.join('&')}`;
  } catch (error) {
    logger.error('Error building deep link:', error);
    return oneLinkURL;
  }
}

const defaultBag: any = {
  displayName: '-',
  distance: null,
  inSalesWindow: true,
  item: {
    itemId: '-',
    itemPrice: null,
    itemValue: null,
    coverPicture: {
      pictureUrl: '-',
    },
    logoPicture: {
      pictureUrl: '-',
    },
    itemCategory: '-',
    name: '-',
    description: '-',
    heroAltText: '-',
    logoAltText: '-',
  },
  itemType: '-',
  itemsAvailable: 3,
  localizationCode: 'es-ES',
  pickupInterval: {
    start: '-',
    end: '-',
  },
  pickupLocation: {
    address: {
      addressLine: '-',
      city: '-',
      country: {
        isoCode: 'ES',
        name: 'España',
      },
      postalCode: '-',
    },
    location: {
      latitude: 41.3839,
      longitude: 2.1761,
    },
  },
  purchaseEnd: '2025-05-27T19:15:00Z',
  soldOutAt: null,
  store: {
    storeId: '-',
    storeName: '-',
    branch: '-',
    description: '-',
    website: '-',
    phone: '-',
    logo: {
      pictureUrl: '-',
      altText: '-',
    },
    rating: {
      average: 4.7,
      count: 124,
    },
  },
};

export function surpriseBagContentPlaceholder(): any {
  return reactive(defaultBag);
};
